import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
// Framer Motion supprimé - utilisation d'animations CSS
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../common/Avatar';
import apiService from '../../services/api';
import { 
  Users, 
  UserCheck, 
  Activity, 
  MapPin, 
  MessageCircle, 
  FileText,
  TrendingUp,
  Heart,
  AlertCircle,
  Clock,
  Building
} from 'lucide-react';

const Dashboard = () => {
  const { user, isOnline } = useAuth();
  const [stats, setStats] = useState([
    {
      title: 'Médecins Actifs',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: UserCheck,
      color: 'bg-blue-500'
    },
    {
      title: 'Patients Enregistrés',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: Users,
      color: 'bg-green-500'
    },
    {
      title: 'Consultations',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: Heart,
      color: 'bg-pink-500'
    },
    {
      title: 'Zones de Santé',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: MapPin,
      color: 'bg-purple-500'
    },
    {
      title: 'Hôpitaux',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: Building,
      color: 'bg-indigo-500'
    },
    {
      title: 'Maladies',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: AlertCircle,
      color: 'bg-red-500'
    },
    {
      title: 'Études',
      value: '0',
      change: '-',
      changeType: 'neutral',
      icon: FileText,
      color: 'bg-orange-500'
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  
  // Fonction pour formater la date de dernière connexion
  const formatLastConnection = (lastLoginDate) => {
    if (!lastLoginDate) {
      return 'Jamais connecté';
    }

    const lastLogin = new Date(lastLoginDate);
    const now = new Date();
    const diffInMs = now - lastLogin;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // Formater l'heure
    const timeStr = lastLogin.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    // Aujourd'hui
    if (diffInDays === 0) {
      return `Aujourd'hui, ${timeStr}`;
    }
    
    // Hier
    if (diffInDays === 1) {
      return `Hier, ${timeStr}`;
    }
    
    // Cette semaine
    if (diffInDays < 7) {
      const dayName = lastLogin.toLocaleDateString('fr-FR', { weekday: 'long' });
      return `${dayName}, ${timeStr}`;
    }
    
    // Plus d'une semaine : afficher la date complète
    return lastLogin.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleSeeAllActivities = async () => {
    try {
      await Swal.fire({
        title: 'Abonnement requis',
        text: 'Pour voir toutes les activités, activez la souscription à 5 $/mois.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Payer 5 $/mois',
        cancelButtonText: 'Plus tard'
      });
    } catch (e) {
      window.alert('Abonnement requis: Pour voir toutes les activités, activez la souscription à 5 $/mois.');
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const role = (user?.role || '').toLowerCase();

        let activitiesResult;
        if (role === 'admin') {
          // Activités récentes globales
          activitiesResult = await apiService.getActivities({ page: 1, limit: 4 }).catch(() => ({ data: [] }));
        } else {
          // Activités récentes de l'utilisateur connecté
          activitiesResult = await apiService.getActivities({ page: 1, limit: 4, userId: user?.id }).catch(() => ({ data: [] }));
        }

        const activities = activitiesResult?.data || [];
        const formattedActivities = activities.map((activity, index) => {
          // Déterminer l'icône et la couleur basés sur le type d'entité
          let icon = FileText;
          let color = 'text-blue-500';
          
          switch (activity.entityType) {
            case 'Consultation':
              icon = Heart;
              color = 'text-red-500';
              break;
            case 'Patient':
              icon = Users;
              color = 'text-blue-500';
              break;
            case 'Doctor':
              icon = UserCheck;
              color = 'text-indigo-500';
              break;
            case 'Hospital':
              icon = Building;
              color = 'text-blue-600';
              break;
            case 'HealthZone':
              icon = MapPin;
              color = 'text-purple-500';
              break;
            case 'Alert':
              icon = AlertCircle;
              color = 'text-yellow-500';
              break;
            case 'Study':
              icon = FileText;
              color = 'text-green-500';
              break;
            case 'Disease':
              icon = AlertCircle;
              color = 'text-red-600';
              break;
            default:
              icon = FileText;
              color = 'text-gray-500';
          }

          // Formatage du message en français
          const actionLabels = {
            'CREATE': 'Nouveau',
            'UPDATE': 'Mis à jour',
            'DELETE': 'Supprimé',
            'VIEW': 'Consulté',
            'CREATE_PATIENT': 'Patient enregistré',
            'CREATE_CONSULTATION': 'Consultation créée',
            'CREATE_PRESCRIPTION': 'Prescription créée'
          };
          
          const actionLabel = actionLabels[activity.action] || activity.action;
          
          // Messages plus descriptifs
          let message = '';
          if (activity.description) {
            message = activity.description;
          } else if (activity.entityName) {
            message = `${actionLabel} ${activity.entityType.toLowerCase()}: ${activity.entityName}`;
          } else {
            message = `${actionLabel} ${activity.entityType}`;
          }

          // Formatage du temps
          const activityDate = new Date(activity.createdAt);
          const now = new Date();
          const diffInMinutes = Math.floor((now - activityDate) / (1000 * 60));
          const diffInHours = Math.floor(diffInMinutes / 60);
          const diffInDays = Math.floor(diffInHours / 24);
          
          let time = 'Récemment';
          if (diffInMinutes < 60) {
            if (diffInMinutes === 0) {
              time = 'À l\'instant';
            } else {
              time = `Il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
            }
          } else if (diffInHours < 24) {
            time = `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
          } else if (diffInDays < 7) {
            time = `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
          } else {
            const diffInWeeks = Math.floor(diffInDays / 7);
            time = `Il y a ${diffInWeeks} semaine${diffInWeeks > 1 ? 's' : ''}`;
          }

          return {
            id: activity.id,
            type: activity.entityType.toLowerCase(),
            message,
            time,
            icon,
            color
          };
        });

        setRecentActivities(formattedActivities);

        if (role === 'admin') {
          // Récupérer les données globales (totaux)
          const [doctorsResult, patientsResult, consultationsResult, healthZonesResult, hospitalsResult, diseasesResult, studiesResult] = await Promise.all([
            apiService.getDoctors().catch(() => ({ data: [] })),
            apiService.getPatients().catch(() => ({ data: [] })),
            apiService.getConsultations().catch(() => ({ data: [] })),
            apiService.getHealthZones().catch(() => ({ data: [] })),
            apiService.getHospitals().catch(() => ({ data: [] })),
            apiService.getDiseases().catch(() => ({ data: [] })),
            apiService.getStudies().catch(() => ({ data: [] }))
          ]);

          const doctorsCount = doctorsResult?.data?.length || 0;
          const patientsCount = patientsResult?.data?.length || 0;
          const consultationsCount = consultationsResult?.data?.length || 0;
          const healthZonesCount = healthZonesResult?.data?.length || 0;
          const hospitalsCount = hospitalsResult?.data?.length || 0;
          const diseasesCount = diseasesResult?.data?.length || 0;
          const studiesCount = studiesResult?.data?.length || 0;

          setStats([
            {
              title: 'Médecins Actifs',
              value: doctorsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: UserCheck,
              color: 'bg-blue-500'
            },
            {
              title: 'Patients Enregistrés',
              value: patientsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Users,
              color: 'bg-green-500'
            },
            {
              title: 'Consultations',
              value: consultationsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Heart,
              color: 'bg-pink-500'
            },
            {
              title: 'Zones de Santé',
              value: healthZonesCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: MapPin,
              color: 'bg-purple-500'
            },
            {
              title: 'Hôpitaux',
              value: hospitalsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Building,
              color: 'bg-indigo-500'
            },
            {
              title: 'Maladies',
              value: diseasesCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: AlertCircle,
              color: 'bg-red-500'
            },
            {
              title: 'Études',
              value: studiesCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: FileText,
              color: 'bg-orange-500'
            }
          ]);
        } else if (role === 'doctor') {
          // Utiliser directement le userId du docteur pour filtrer assignedDoctorId
          const doctorUserId = user?.id;

          // Compter les consultations effectuées par ce médecin
          // Récupérer le doctorId pour les consultations
          const doctorsList = await apiService.getDoctors().catch(() => ({ data: [] }));
          const meAsDoctor = doctorsList?.data?.find((d) => d?.user?.id === user?.id);
          const doctorId = meAsDoctor?.id;

          const myConsultations = await apiService.getConsultations({ doctorId, limit: 1000 }).catch(() => ({ data: [] }));
          const myConsultationsCount = myConsultations?.data?.length || 0;

          // Compter les patients assignés à ce médecin via assignedDoctorId (docteur_assigne_id)
          // Utiliser le userId du docteur au lieu du doctorId
          const myPatients = await apiService.getPatientsByDoctor(doctorUserId).catch(() => ({ data: [] }));
          const myPatientsCount = myPatients?.data?.length || 0;

          setStats([
            {
              title: 'Mes Patients',
              value: myPatientsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Users,
              color: 'bg-green-500'
            },
            {
              title: 'Mes Consultations',
              value: myConsultationsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Heart,
              color: 'bg-pink-500'
            }
          ]);
        } else if (role === 'patient') {
          // Identifier le patientId de l'utilisateur pour compter ses consultations
          const patientsList = await apiService.getPatients().catch(() => ({ data: [] }));
          const meAsPatient = patientsList?.data?.find((p) => p?.user?.id === user?.id);
          const patientId = meAsPatient?.id;

          const myConsultations = await apiService.getConsultations({ patientId, limit: 1000 }).catch(() => ({ data: [] }));
          const myConsultationsCount = myConsultations?.data?.length || 0;

          setStats([
            {
              title: 'Mes Consultations',
              value: myConsultationsCount.toLocaleString(),
              change: '-',
              changeType: 'neutral',
              icon: Heart,
              color: 'bg-pink-500'
            }
          ]);
        } else {
          // Rôle non spécifié: n'afficher que les consultations de l'utilisateur s'il y en a, sinon aucun total
          setStats([]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const quickActions = [
    {
      title: 'Nouveau Patient',
      description: 'Enregistrer un nouveau patient',
      icon: Users,
      color: 'bg-blue-500 hover:bg-blue-600',
      href: '/patients'
    },
    {
      title: 'Chat Spécialiste',
      description: 'Communiquer avec un spécialiste',
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600',
      href: '/chat'
    },
    {
      title: 'Carte Interactive',
      description: 'Voir les zones de santé',
      icon: MapPin,
      color: 'bg-purple-500 hover:bg-purple-600',
      href: '/map'
    },
    {
      title: 'Rapports PDF',
      description: 'Générer des rapports',
      icon: FileText,
      color: 'bg-orange-500 hover:bg-orange-600',
      href: '/reports'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        
        
        className="bg-white shadow-sm border-b border-gray-200"
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bonjour, {user?.name} 👋
              </h1>
              <p className="text-gray-600">Voici un aperçu de votre système HealthBridge</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Dernière connexion</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatLastConnection(user?.lastLogin)}
                </p>
              </div>
              <Avatar
                src={user?.avatar}
                alt={user?.name}
                name={user?.name}
                size="lg"
                className="ring-2 ring-primary-500"
                isOnline={isOnline}
                showOnlineStatus={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {loading ? (
            // État de chargement
            [...Array(7)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Statistiques réelles
            stats.map((stat, index) => (
              <div
                key={stat.title}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    <div className="flex items-center mt-2">
                      <span className="text-sm text-gray-500">Données en temps réel</span>
                    </div>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div
            
            
            
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
              <div className="space-y-3">
                {quickActions.map((action, index) => (
                  <a
                    key={action.title}
                    href={action.href}
                    className={`${action.color} text-white p-4 rounded-lg flex items-center space-x-3 transition-all duration-200 cursor-pointer`}
                  >
                    <action.icon className="h-5 w-5" />
                    <div>
                      <p className="font-medium">{action.title}</p>
                      <p className="text-sm opacity-90">{action.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div
            
            
            
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Activités Récentes</h3>
                <button className="text-primary-600 hover:text-primary-700 text-sm font-medium" onClick={handleSeeAllActivities}>
                  Voir tout
                </button>
              </div>
              <div className="space-y-4">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    >
                      <div className={`${activity.color} p-2 rounded-lg`}>
                        <activity.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                        <div className="flex items-center mt-1">
                          <Clock className="h-3 w-3 text-gray-400 mr-1" />
                          <span className="text-xs text-gray-500">{activity.time}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Aucune activité récente
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
